import { useQuery } from "@tanstack/react-query";
import { metricsApi } from "../api/metrics";
import { weatherApi } from "../api/weather";
import { usageApi } from "../api/usage";
import { qk } from "../../lib/queryKeys";
import type {
  MetricsStatsParams,
  MetricsTrendsParams,
  MetricsDistributionParams,
  MetricsPatternsParams,
  MetricsComparisonParams,
  MetricsFilters,
} from "../api/types";

// 30 minutes — overall range data rarely changes between sessions
const OVERALL_RANGE_STALE_TIME = 30 * 60 * 1000;

export const useMetricsStats = (params?: MetricsStatsParams) => {
  const metrics = params?.metrics;
  return useQuery({
    queryKey: qk.metrics.stats(params),
    queryFn: () => metricsApi.getStats(params),
    staleTime: 0, // Always fetch fresh data for real-time updates
    enabled: !!params?.user_ids?.length || params?.client_id !== undefined, // null = all clients (valid), undefined = not yet initialised
  });
};

export const useMetricsTrends = (params: MetricsTrendsParams) => {
  return useQuery({
    queryKey: qk.metrics.trends(params),
    queryFn: () => metricsApi.getTrends(params),
    staleTime: 0, // Always fetch fresh data for real-time updates
    enabled: !!params?.user_ids?.length || params?.client_id !== undefined, // null = all clients (valid), undefined = not yet initialised
  });
};

export const useWeatherTrends = (params: MetricsFilters & { enabled?: boolean }) => {
  return useQuery({
    queryKey: qk.weatherTrends(params),
    queryFn: () => weatherApi.getTrends(params),
    staleTime: 10 * 60 * 1000, // weather changes slowly; cache 10 min
    enabled:
      (params.enabled ?? true) &&
      (!!params?.user_ids?.length || params?.client_id !== undefined),
  });
};

export const useUsageStats = (params: MetricsFilters & { enabled?: boolean }) => {
  return useQuery({
    queryKey: qk.usageStats(params),
    queryFn: () => usageApi.getStats(params),
    staleTime: 5 * 60 * 1000,
    enabled:
      (params.enabled ?? true) &&
      (!!params?.user_ids?.length || params?.client_id !== undefined),
  });
};

export const useMetricsDistribution = (params: MetricsDistributionParams) => {
  return useQuery({
    queryKey: qk.metrics.distribution(params.metric, params),
    queryFn: () => metricsApi.getDistribution(params),
    staleTime: 0, // Always fetch fresh data for real-time updates
    enabled: !!params?.user_ids?.length || params?.client_id !== undefined, // null = all clients (valid), undefined = not yet initialised
  });
};

export const useMetricsPatterns = (params: MetricsPatternsParams) => {
  return useQuery({
    queryKey: qk.metrics.patterns(params.metric, params),
    queryFn: () => metricsApi.getPatterns(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useMetricsComparison = (params: MetricsComparisonParams) => {
  return useQuery({
    queryKey: qk.metrics.comparison(params),
    queryFn: () => metricsApi.getComparison(params),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetches the absolute all-time MIN and MAX for all metrics scoped to
 * the given clientId.  This ignores date range, department, age, sex, and
 * all other filters — it returns the lowest/highest individual values ever
 * recorded for this client.
 *
 * The result is cached for 30 minutes because it changes infrequently and
 * should not be re-fetched when the user changes dashboard filters.
 */
export const useMetricsOverallRange = (clientId: string | null | undefined) => {
  return useQuery({
    queryKey: qk.metrics.overallRange(clientId),
    // null = "All Clients" — send no client_id so the API queries across all data
    queryFn: () => metricsApi.getOverallRange({ client_id: clientId ?? undefined }),
    staleTime: OVERALL_RANGE_STALE_TIME,
    // Enable for specific client (string) OR all clients (null). Only block when uninitialised (undefined).
    enabled: clientId !== undefined,
  });
};
